import React, { useEffect } from 'react';
import Dialog from 'src/components/common/Dialog';
import Button from 'src/components/controls/forms/redesign/FormButton';
import { useForm } from 'react-hook-form';
import RHFSelect from '../../controls/tempRHFControls/RHFSelect';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import DialogList from 'src/dialogs';
import Invoice from 'src/components/project-billing/models/InvoiceMaster';
import CreateInvoiceValidationResult from 'src/components/project-billing/models/CreateInvoiceValidation';

interface ICreateInvoiceDialogProps {
    client?: number;
    project?: number;
    subcode?: number;
    altSubcode?: number;
    dialogName: string;
    handleSubmit?: (payload: Invoice) => Promise<CreateInvoiceValidationResult>;
    hideDialog: () => void;
    showDialog: (dialogName: DialogList) => void;
    offices?: Array<{value: number | string, label: string}>;
}

type CreateInvoiceFormValues = {
    createInvoice: {
        beginDate: string;
        endDate: string;
        invoiceDate: string;
        clientNumber: number;
        projectNumber: number;
        subCode: number;
        altSubcode: string;
        office: string | number;
        officeString: string | number;
        payPeriod: string;
        validationMessage: string;
        validationCode: string;
    };
};

const CreateInvoiceDialog = (props: ICreateInvoiceDialogProps) => {
    const { register, errors, getValues, setValue, setError, clearErrors } = useForm();

    useEffect(() => {
        onCreate();
    }, []);

    useEffect(() => {
        if (errors?.officeString) {
            const officeLabel = errors?.officeString?.message;
            const officeValue =  props?.offices?.find(o => o.label === officeLabel)?.value;
            setValue(`createInvoice.office`, officeValue);
        }
    }, [errors?.officeString]);

    const onCreate = () => {
        console.log('ReportOptionsDialog onCreate()');
    };

    const OKClicked = async () => {
        clearErrors();
        let data = getValues();
        const payload: Partial<Invoice> = {
            ...data.createInvoice,
            source: 'M'
        };
        payload.creationTime = new Date(payload.creationTime);

        const result = await props.handleSubmit(payload);

        if (!result.isValid && result.fieldErrors) {
            Object.entries(result.fieldErrors).forEach(([field, message]) => {
                setError(field as keyof CreateInvoiceFormValues, {
                    type: 'server',
                    message
                });
            });
            return;
        } else {
            props.hideDialog();
        }
    };

    const getPeriodDate = (isEnd: boolean) => {
        const today = new Date();
        const year = today.getFullYear();
        const month = today.getMonth(); // 0-based (Jan = 0)
        const dayOfMonth = today.getDate();

        let day: number;

        if (!isEnd) {
          // Start of the period
          day = dayOfMonth < 16 ? 1 : 16;
        } else {
          // End of the period
          day = dayOfMonth <= 16
            ? 16
            : new Date(year, month + 1, 0).getDate(); // last day of month
        }

        const periodDate = new Date(year, month, day);
        return periodDate.toISOString().split('T')[0];
    };

    return (
        <Dialog title="Create Invoice" dialogName={props.dialogName} isExitButtonInvisible={true} bodyStyle={{ width: '20%' }}>
            <div style={{ display: 'flex', justifyContent: 'right' }}>
                <form>
                    <RHFInputBox
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '9rem'}}
                        label="Start Date"
                        labelOnLeft={true}
                        defaultVal={getPeriodDate(false)}
                        name={`createInvoice.beginDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '30%' }}
                    />
                    <RHFInputBox
                        label="End Date"
                        labelOnLeft={true}
                        defaultVal={getPeriodDate(true)}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '9.5rem'}}
                        name={`createInvoice.endDate`}
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '30%' }}
                    />
                    <RHFInputBox
                        label="Invoice Date"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '7.3rem'}}
                        name={`createInvoice.invoiceDate`}
                        defaultVal={new Date().toISOString().split('T')[0]}
                        type="date"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />
                    <div style={{ display: 'flex', justifyContent: 'left', flexDirection: 'row' }}>
                        <RHFInputBox
                            label="Client Number"
                            labelOnLeft={true}
                            labelStyle={{whiteSpace: 'nowrap', marginRight: '9.8rem'}}
                            name={`createInvoice.clientNumber`}
                            type="number"
                            defaultVal={!!props?.client ? props?.client?.toString() : ''}
                            register={register}
                            errors={errors}
                            style={{ width: '100%' }}
                            bodyStyle={{ width: '70%' }}
                        />
                        {errors?.clientNumber && <p style={{ marginLeft: '.5rem', color: 'red', whiteSpace: 'nowrap' }}>{errors?.clientNumber?.message}</p>}
                    </div>
                    <RHFInputBox
                        label="Project Number"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '9.1rem'}}
                        name={`createInvoice.projectNumber`}
                        type="number"
                        defaultVal={!!props?.project ? props?.project?.toString() : ''}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />
                        <RHFInputBox
                            label="Subcode"
                            labelOnLeft={true}
                            labelStyle={{whiteSpace: 'nowrap', marginRight: '13.4rem'}}
                            name={`createInvoice.subCode`}
                            type="number"
                            register={register}
                            errors={errors}
                            style={{
                                width: '100%',
                                boxShadow: errors?.subcode ? '0 0 0 1px red' : undefined
                            }}
                            bodyStyle={{ width: '70%' }}
                        />
                    <RHFInputBox
                        label="Alt Subcode"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '11.3rem'}}
                        name={`createInvoice.altSubcode`}
                        type="text"
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />
                    <RHFSelect
                        label="Billing Office"
                        labelOnLeft={true}
                        labelStyle={{whiteSpace: 'nowrap', marginRight: '11rem'}}
                        name={`createInvoice.office`}
                        options={!!props?.offices && props?.offices?.length > 0 ? props.offices : [
                            { value: '1', label: 'OK' },
                            { value: '2', label: 'TX' },
                            { value: '3', label: 'IH' }
                        ]}
                        register={register}
                        errors={errors}
                        style={{ width: '100%' }}
                        bodyStyle={{ width: '70%' }}
                    />
                </form>
            </div>
            <div className="form-row" style={{marginTop: '2rem', justifyContent: 'center'}}>
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Create Invoice'}
                    onClick={() => OKClicked()}
                />
                <Button
                    className="blue-hover-button"
                    style={{ width: '24%', marginRight: '1rem' }}
                    text={'Cancel'}
                    onClick={() => props.hideDialog()}
                />
            </div>
            {errors?.subcode && <p style={{ marginLeft: '.5rem', color: 'red'}}>{errors?.subcode?.message}</p>}
            {errors?.office && <p style={{ marginLeft: '.5rem', color: 'red'}}>{errors?.office?.message}</p>}
            {errors?.payPeriod && <p style={{ marginLeft: '.5rem', color: 'red'}}>{errors?.payPeriod?.message}</p>}
            {errors?.validationCode && <p style={{ marginLeft: '.5rem', color: 'red'}}>{errors?.validationMessage?.message}</p>}
        </Dialog>
    );
};

export default CreateInvoiceDialog;



// WEBPACK FOOTER //
// ./src/components/project-billing/dialogs/CreateInvoiceDialog.tsx